import { storeAudioBlob } from './media';
import type { MediaAsset } from '@/types';

const MANIFEST_URL = '/assets/music/manifest.json';
const TRACK_BASE_URL = '/assets/music/';

export interface MusicLibraryTrack {
  id: string;
  title: string;
  file: string;
  mood: string;
  durationSeconds: number;
}

let cachedManifest: MusicLibraryTrack[] | null = null;

/** Fetches (and caches) the list of bundled music tracks from public/assets/music/manifest.json. */
export async function fetchMusicLibrary(): Promise<MusicLibraryTrack[]> {
  if (cachedManifest) return cachedManifest;
  const response = await fetch(MANIFEST_URL);
  if (!response.ok) throw new Error('Could not load the bundled music library.');
  const tracks = (await response.json()) as MusicLibraryTrack[];
  cachedManifest = tracks;
  return tracks;
}

export function musicLibraryTrackUrl(track: MusicLibraryTrack): string {
  return `${TRACK_BASE_URL}${track.file}`;
}

/**
 * Downloads a bundled library track and stores it as a normal MediaAsset in
 * IndexedDB, so it behaves identically to an uploaded track everywhere else
 * in the app (preview mixing, ducking, export) — no special-casing needed
 * beyond this one fetch step.
 */
export async function selectMusicLibraryTrack(track: MusicLibraryTrack): Promise<MediaAsset> {
  const response = await fetch(musicLibraryTrackUrl(track));
  if (!response.ok) throw new Error(`Could not load "${track.title}".`);
  const blob = await response.blob();
  return storeAudioBlob(blob, `${track.title}.wav`);
}
